
import { defineComponent, ref, computed, onMounted } from 'vue';
import {
  serverOutline,
  libraryOutline,
  gitNetworkOutline,
  codeSlashOutline,
  peopleOutline,
  shieldCheckmarkOutline,
} from 'ionicons/icons';

interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  status: string;
  statusColor: string;
  topics: string[];
  icon: string;
  logo?: string;
  screenshot?: string;
}

interface Service {
  id: string;
  name: string;
  tagline: string;
  description: string;
  icon: string;
  topics: string[];
}

interface ProductsData {
  sectionTitle: string;
  sectionDescription: string;
  products: Product[];
  services: Service[];
}

const iconMap: Record<string, string> = {
  'server-outline': serverOutline,
  'library-outline': libraryOutline,
  'git-network-outline': gitNetworkOutline,
  'code-slash-outline': codeSlashOutline,
  'people-outline': peopleOutline,
  'shield-checkmark-outline': shieldCheckmarkOutline,
};

export default defineComponent({
  name: 'LocalProducts',
  props: {
    apiUrl: {
      type: String,
      default: '/data/products.json'
    },
    branding: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    // Reactive data
    const loading = ref(true);
    const error = ref<string | null>(null);
    const products = ref<Product[]>([]);
    const services = ref<Service[]>([]);
    const sectionDescription = ref('');

    // Computed properties
    const showBranding = computed(() => props.branding);

    const getIcon = (iconName: string): string => {
      return iconMap[iconName] || serverOutline;
    };

    const statusBadgeStyle = (color: string) => {
      return {
        backgroundColor: `${color}20`,
        color: color,
        borderColor: `${color}40`,
      };
    };

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;

        const response = await fetch(props.apiUrl);
        if (!response.ok) {
          throw new Error(`Failed to load products data: ${response.status}`);
        }

        const data: ProductsData = await response.json();
        products.value = data.products;
        services.value = data.services || [];
        sectionDescription.value = data.sectionDescription;

      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load products data';
        console.error('Products Data Error:', err);
      } finally {
        loading.value = false;
      }
    };

    // Lifecycle
    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      error,
      products,
      services,
      sectionDescription,
      showBranding,
      getIcon,
      statusBadgeStyle,
    };
  }
});
